/**
 * Draft room allocation and helper utilities.
 *
 * Rooms are sized from the season contestant count (and an optional manager
 * room-count choice). Teams are split evenly across rooms, with a soft
 * preference to keep teams that share claimers together.
 */

export const DRAFT_STATUS = {
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'inProgress',
  COMPLETE: 'complete',
  /** All picks in; commissioner has locked the draft (no undo, read-only). */
  FINALIZED: 'finalized',
};

export const DRAFT_ROOM_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'inProgress',
  COMPLETE: 'complete',
};

/**
 * Fewest rooms that keep every room at or under the contestant pool size.
 * @param {number} teamCount
 * @param {number} contestantCount
 * @returns {number}
 */
export function getMinDraftRoomCount(teamCount, contestantCount) {
  const T = Math.max(0, Number(teamCount) || 0);
  const C = Math.max(1, Number(contestantCount) || 1);
  if (T === 0) return 0;
  return Math.max(1, Math.ceil(T / C));
}

/**
 * Compute how many rooms and how many teams per room.
 * When roomCount is omitted, uses the fewest valid rooms (largest rooms).
 * Invalid explicit counts are clamped to [minRooms, teamCount].
 * @param {number} teamCount
 * @param {number} contestantCount
 * @param {number} [roomCount]
 * @returns {{ roomCount: number, roomSizes: number[] }}
 */
export function computeRoomSizes(teamCount, contestantCount, roomCount) {
  const T = Math.max(0, Number(teamCount) || 0);
  const C = Math.max(1, Number(contestantCount) || 1);
  if (T === 0) {
    return { roomCount: 0, roomSizes: [] };
  }
  const minRooms = getMinDraftRoomCount(T, C);
  const maxRooms = T;
  const requested = Number(roomCount);
  const n =
    roomCount == null || !Number.isFinite(requested)
      ? minRooms
      : Math.min(maxRooms, Math.max(minRooms, Math.floor(requested)));
  const base = Math.floor(T / n);
  const extra = T % n;
  const roomSizes = Array.from(
    { length: n },
    (_, i) => base + (i < extra ? 1 : 0)
  );
  return { roomCount: n, roomSizes };
}

/**
 * Dropdown label for a room split (even or a min–max range).
 * @param {number} roomCount
 * @param {number[]} roomSizes
 * @returns {string}
 */
export function formatDraftRoomOptionLabel(roomCount, roomSizes) {
  const roomWord = roomCount === 1 ? 'room' : 'rooms';
  const unique = [...new Set(roomSizes)];
  if (unique.length === 1) {
    const size = unique[0];
    const teamWord = size === 1 ? 'team' : 'teams';
    return `${roomCount} ${roomWord} · ${size} ${teamWord} each`;
  }
  const minSize = Math.min(...roomSizes);
  const maxSize = Math.max(...roomSizes);
  return `${roomCount} ${roomWord} · ${minSize}–${maxSize} teams each`;
}

/**
 * Up to four consecutive valid room-count splits, starting at the minimum.
 * @param {number} teamCount
 * @param {number} contestantCount
 * @param {number} [maxOptions=4]
 * @returns {Array<{ roomCount: number, roomSizes: number[], label: string }>}
 */
export function getDraftRoomCountOptions(
  teamCount,
  contestantCount,
  maxOptions = 4
) {
  const T = Math.max(0, Number(teamCount) || 0);
  const C = Math.max(1, Number(contestantCount) || 1);
  if (T === 0) return [];
  const minRooms = getMinDraftRoomCount(T, C);
  const maxRooms = T;
  const limit = Math.max(1, Number(maxOptions) || 4);
  const options = [];
  for (let n = minRooms; n <= maxRooms && options.length < limit; n += 1) {
    const { roomSizes } = computeRoomSizes(T, C, n);
    options.push({
      roomCount: n,
      roomSizes,
      label: formatDraftRoomOptionLabel(n, roomSizes),
    });
  }
  return options;
}

/**
 * Fisher-Yates shuffle (returns new array).
 * @template T
 * @param {T[]} items
 * @param {() => number} [random]
 * @returns {T[]}
 */
export function shuffleArray(items, random = Math.random) {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/**
 * Build soft groups of team IDs that share at least one claimer.
 * @param {string[]} teamIds
 * @param {Object.<string, string[]>} claimersByTeamId - teamId -> userIds
 * @returns {string[][]} Connected components (largest first)
 */
export function buildClaimerSoftGroups(teamIds, claimersByTeamId = {}) {
  const ids = [...teamIds];
  const parent = Object.fromEntries(ids.map(id => [id, id]));

  function find(x) {
    if (parent[x] !== x) parent[x] = find(parent[x]);
    return parent[x];
  }
  function union(a, b) {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  }

  // userId -> teamIds claimed by that user
  const teamsByUser = {};
  for (const teamId of ids) {
    const claimers = claimersByTeamId[teamId] || [];
    for (const userId of claimers) {
      if (!teamsByUser[userId]) teamsByUser[userId] = [];
      teamsByUser[userId].push(teamId);
    }
  }

  for (const teamList of Object.values(teamsByUser)) {
    for (let i = 1; i < teamList.length; i += 1) {
      union(teamList[0], teamList[i]);
    }
  }

  // Also link teams whose claimer sets overlap (shared family accounts)
  for (let i = 0; i < ids.length; i += 1) {
    const aClaimers = new Set(claimersByTeamId[ids[i]] || []);
    if (aClaimers.size === 0) continue;
    for (let j = i + 1; j < ids.length; j += 1) {
      const bClaimers = claimersByTeamId[ids[j]] || [];
      if (bClaimers.some(uid => aClaimers.has(uid))) {
        union(ids[i], ids[j]);
      }
    }
  }

  const groupsMap = {};
  for (const id of ids) {
    const root = find(id);
    if (!groupsMap[root]) groupsMap[root] = [];
    groupsMap[root].push(id);
  }

  return Object.values(groupsMap).sort((a, b) => b.length - a.length);
}

/**
 * Allocate teams into draft rooms with even sizing and soft claimer grouping.
 * @param {Array<{id: string}>|string[]} teams
 * @param {number} contestantCount
 * @param {Object.<string, string[]>} [claimersByTeamId]
 * @param {() => number} [random]
 * @param {number} [roomCount] - When omitted, uses the fewest valid rooms
 * @returns {Array<{ id: number, teamOrder: string[], currentPickIndex: number, status: string, pickHistory: Array }>}}
 */
export function allocateTeamsToDraftRooms(
  teams,
  contestantCount,
  claimersByTeamId = {},
  random = Math.random,
  roomCount
) {
  const teamIds = (teams || []).map(t => (typeof t === 'string' ? t : t.id));
  const { roomCount: count, roomSizes } = computeRoomSizes(
    teamIds.length,
    contestantCount,
    roomCount
  );
  if (count === 0) return [];

  const softGroups = buildClaimerSoftGroups(teamIds, claimersByTeamId).map(
    group => shuffleArray(group, random)
  );

  const rooms = roomSizes.map(() => []);
  const remainingCapacity = [...roomSizes];

  // Place largest soft groups first into the room with most remaining space
  // that can fit the whole group; otherwise split across rooms.
  for (const group of softGroups) {
    let remaining = [...group];
    while (remaining.length > 0) {
      let bestIdx = -1;
      let bestCap = -1;
      for (let i = 0; i < rooms.length; i += 1) {
        if (remainingCapacity[i] > bestCap) {
          bestCap = remainingCapacity[i];
          bestIdx = i;
        }
      }
      if (bestIdx < 0 || bestCap <= 0) break;

      const take = Math.min(remaining.length, remainingCapacity[bestIdx]);
      rooms[bestIdx].push(...remaining.slice(0, take));
      remainingCapacity[bestIdx] -= take;
      remaining = remaining.slice(take);
    }
  }

  return rooms.map((teamOrder, index) => ({
    id: index + 1,
    teamOrder: shuffleArray(teamOrder, random),
    currentPickIndex: 0,
    status: DRAFT_ROOM_STATUS.PENDING,
    pickHistory: [],
  }));
}

/**
 * @param {Object} room
 * @returns {string|null} teamId whose turn it is, or null if room done/empty
 */
export function getCurrentTeamId(room) {
  if (!room || !Array.isArray(room.teamOrder)) return null;
  if (room.status === DRAFT_ROOM_STATUS.COMPLETE) return null;
  const idx = room.currentPickIndex ?? 0;
  if (idx < 0 || idx >= room.teamOrder.length) return null;
  return room.teamOrder[idx] || null;
}

/**
 * Contestants already drafted in a room (from pickHistory or team draftPicks).
 * @param {Object} room
 * @param {Array} [teams] - optional teams with draftPicks for fallback
 * @returns {Set<string>}
 */
export function getTakenContestantIds(room, teams = []) {
  const taken = new Set();
  if (room?.pickHistory?.length) {
    for (const pick of room.pickHistory) {
      if (pick.contestantId) taken.add(pick.contestantId);
    }
    return taken;
  }
  const roomId = room?.id;
  for (const team of teams) {
    for (const pick of team.draftPicks || []) {
      const pickRoom = pick.draftRoomId ?? pick.weekPicked ?? null;
      if (pickRoom === roomId && pick.contestantId) {
        taken.add(pick.contestantId);
      }
    }
  }
  return taken;
}

/**
 * Whether a team already has a pick in this room.
 * @param {Object} team
 * @param {number} roomId
 * @returns {boolean}
 */
export function teamHasPickInRoom(team, roomId) {
  if (!team?.draftPicks?.length) return false;
  return team.draftPicks.some(p => (p.draftRoomId ?? p.weekPicked) === roomId);
}

/**
 * Build a draft pick document for a team (includes legacy field aliases).
 * @param {{ contestantId: string, draftRoomId: number, pickNumber: number }} params
 */
export function buildTeamDraftPick({ contestantId, draftRoomId, pickNumber }) {
  return {
    contestantId,
    draftRoomId,
    pickNumber,
    // Legacy aliases used by older UI / offline draft scripts
    weekPicked: draftRoomId,
    draftId: pickNumber,
  };
}

/**
 * Find which room a team belongs to.
 * @param {Array} draftRooms
 * @param {string} teamId
 * @returns {Object|null}
 */
export function findRoomForTeam(draftRooms, teamId) {
  if (!draftRooms || !teamId) return null;
  return draftRooms.find(r => r.teamOrder?.includes(teamId)) || null;
}

/**
 * Pick position (1-based) for a team in its room order.
 * @param {Object} room
 * @param {string} teamId
 * @returns {number|null}
 */
export function getTeamPickPosition(room, teamId) {
  if (!room?.teamOrder) return null;
  const idx = room.teamOrder.indexOf(teamId);
  return idx >= 0 ? idx + 1 : null;
}

/**
 * Move a team from one room to another (pre-start editing).
 * @param {Array} draftRooms
 * @param {string} teamId
 * @param {number} toRoomId
 * @returns {Array} new draftRooms array
 */
export function moveTeamBetweenRooms(draftRooms, teamId, toRoomId) {
  const rooms = draftRooms.map(r => ({
    ...r,
    teamOrder: [...(r.teamOrder || [])],
    pickHistory: [...(r.pickHistory || [])],
  }));
  let moved = null;
  for (const room of rooms) {
    const idx = room.teamOrder.indexOf(teamId);
    if (idx >= 0) {
      moved = room.teamOrder.splice(idx, 1)[0];
      break;
    }
  }
  if (!moved) return draftRooms;
  const dest = rooms.find(r => r.id === toRoomId);
  if (!dest) return draftRooms;
  dest.teamOrder.push(moved);
  return rooms;
}

/**
 * Reorder teams within a room.
 * @param {Array} draftRooms
 * @param {number} roomId
 * @param {string[]} newTeamOrder
 * @returns {Array}
 */
export function reorderRoomTeams(draftRooms, roomId, newTeamOrder) {
  return draftRooms.map(r =>
    r.id === roomId ? { ...r, teamOrder: [...newTeamOrder] } : r
  );
}

/**
 * Shuffle draft order within every room (or one room).
 * @param {Array} draftRooms
 * @param {number|null} [roomId]
 * @param {() => number} [random]
 * @returns {Array}
 */
export function reshuffleRoomOrders(
  draftRooms,
  roomId = null,
  random = Math.random
) {
  return draftRooms.map(r => {
    if (roomId != null && r.id !== roomId) return r;
    return { ...r, teamOrder: shuffleArray(r.teamOrder || [], random) };
  });
}

/**
 * Whether every room is complete.
 * @param {Array} draftRooms
 * @returns {boolean}
 */
export function areAllRoomsComplete(draftRooms) {
  if (!draftRooms?.length) return false;
  return draftRooms.every(
    r =>
      r.status === DRAFT_ROOM_STATUS.COMPLETE ||
      (r.teamOrder?.length > 0 &&
        (r.currentPickIndex ?? 0) >= r.teamOrder.length)
  );
}

/**
 * Whether the draft is locked (no picks, no undo).
 * @param {string} draftStatus
 * @returns {boolean}
 */
export function isDraftReadOnly(draftStatus) {
  return draftStatus === DRAFT_STATUS.FINALIZED;
}

/**
 * Whether undo is allowed for the current draft status.
 * @param {string} draftStatus
 * @returns {boolean}
 */
export function canUndoDraftPick(draftStatus) {
  return (
    draftStatus === DRAFT_STATUS.IN_PROGRESS ||
    draftStatus === DRAFT_STATUS.COMPLETE
  );
}
